/**
 * Sar.js — Subject Access Request tracker.
 *
 * GDPR Articles 12-22 require operators to:
 *   - acknowledge user data requests within 30 days,
 *   - prove the request was fulfilled (or rejected, or extended),
 *   - keep an audit trail of who handled the request.
 *
 * This page is the visible record. Each row is a request the team is
 * working on. Status: pending → in_progress → fulfilled / rejected /
 * extended. Deadlines are set automatically (30 days from create, 60
 * more days when status moves to extended).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { FiPlus, FiCheck, FiX, FiClock, FiAlertCircle } from 'react-icons/fi';
import {
  Card, Table, Badge, Button, Input, Modal, Pagination, CsvExportButton,
} from '../../components/UI';
import { usePagination } from '../../hooks';
import {
  listSarRequests, createSarRequest, updateSarRequest,
  SAR_TYPES,
} from '../../services/adminService';

const SAR_TYPE_LABELS = {
  access:       'Access (Art. 15)',
  export:       'Export / portability (Art. 20)',
  rectify:      'Rectification (Art. 16)',
  erase:        'Erasure / right to be forgotten (Art. 17)',
  restrict:     'Restriction (Art. 18)',
  object:       'Object to processing (Art. 21)',
  portability:  'Portability (Art. 20)',
};

const SAR_STATUS_VARIANTS = {
  pending: 'warning',
  in_progress: 'info',
  fulfilled: 'success',
  rejected: 'danger',
  extended: 'warning',
};

const fmt = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : (ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts));
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const Sar = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [error, setError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [draftUserId, setDraftUserId] = useState('');
  const [draftType, setDraftType] = useState('export');
  const [draftSource, setDraftSource] = useState('email');
  const [draftNotes, setDraftNotes] = useState('');
  const [creating, setCreating] = useState(false);

  const [updating, setUpdating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await listSarRequests({ status: filter, maxResults: 200 });
      setItems(r.requests || []);
    } catch (e) {
      setError(e.message || 'Failed to load SARs');
    } finally { setLoading(false); }
  };

  // refresh() closes over `filter`, which is the only dependency that matters;
  // including the function itself would re-subscribe on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [filter]);

  const filtered = useMemo(() => items, [items]);
  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(filtered, 25);

  const handleCreate = async () => {
    if (!draftUserId.trim() || !draftType) {
      setError('User UID and type are required');
      return;
    }
    setCreating(true);
    setError('');
    try {
      await createSarRequest({
        targetUserId: draftUserId.trim(),
        type: draftType,
        source: draftSource,
        notes: draftNotes || undefined,
      });
      setDraftUserId('');
      setDraftType('export');
      setDraftSource('email');
      setDraftNotes('');
      setShowCreate(false);
      await refresh();
    } catch (e) {
      setError(`Create failed: ${e.message}`);
    } finally { setCreating(false); }
  };

  const handleStatusChange = async (row, status) => {
    if (status === 'rejected' || status === 'fulfilled') {
      if (!window.confirm(`Mark this SAR as ${status}?`)) return;
    }
    setUpdating(true);
    try {
      await updateSarRequest({ sarRequestId: row.id, status });
      await refresh();
    } catch (e) {
      setError(`Update failed: ${e.message}`);
    } finally { setUpdating(false); }
  };

  const csvColumns = [
    { key: 'id', label: 'ID' },
    { key: 'targetUserId', label: 'User UID' },
    { key: 'targetEmail', label: 'Email' },
    { key: 'type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Created', accessor: (r) => fmt(r.createdAt) },
    { key: 'deadlineAt', label: 'Deadline', accessor: (r) => fmt(r.deadlineAt) },
    { key: 'closedAt', label: 'Closed', accessor: (r) => fmt(r.closedAt) },
  ];

  const columns = [
    { key: 'targetEmail', title: 'User',
      render: (_, row) => row.targetEmail || `${(row.targetUserId || '').slice(0, 12)}…` },
    { key: 'type', title: 'Request',
      render: (v) => SAR_TYPE_LABELS[v] || v },
    { key: 'status', title: 'Status',
      render: (v) => <Badge variant={SAR_STATUS_VARIANTS[v] || 'inactive'}>{v}</Badge> },
    { key: 'createdAt', title: 'Created', render: (v) => fmt(v) },
    { key: 'deadlineAt', title: 'Deadline',
      render: (v, row) => {
        if (!v) return '—';
        const ms = v.toMillis ? v.toMillis() : (v._seconds * 1000);
        const overdue = ms < Date.now() && row.status !== 'fulfilled' && row.status !== 'rejected';
        return (
          <span style={{ color: overdue ? '#f87171' : undefined, fontWeight: overdue ? 600 : 400 }}>
            {fmt(v)}{overdue ? ' (OVERDUE)' : ''}
          </span>
        );
      } },
    { key: 'actions', title: '', width: '170px',
      render: (_, row) => {
        const closed = row.status === 'fulfilled' || row.status === 'rejected';
        if (closed) {
          return <span style={{ color: '#888', fontSize: 12 }}>closed {fmt(row.closedAt)}</span>;
        }
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            {row.status === 'pending' && (
              <Button size="sm" variant="ghost" icon={<FiClock />} disabled={updating}
                onClick={() => handleStatusChange(row, 'in_progress')} title="Mark in progress" />
            )}
            <Button size="sm" variant="primary" icon={<FiCheck />} disabled={updating}
              onClick={() => handleStatusChange(row, 'fulfilled')} title="Fulfilled" />
            <Button size="sm" variant="ghost" icon={<FiAlertCircle />} disabled={updating}
              onClick={() => handleStatusChange(row, 'extended')} title="Extend (+60 days)" />
            <Button size="sm" variant="danger" icon={<FiX />} disabled={updating}
              onClick={() => handleStatusChange(row, 'rejected')} title="Reject" />
          </div>
        );
      } },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0 }}>Subject Access Requests</h2>
            <p style={{ margin: '4px 0 0 0', color: '#888', fontSize: 13 }}>
              GDPR Art. 12 — 30-day response deadline. Track every user data request through to fulfillment.
            </p>
          </div>
          <Button variant="primary" icon={<FiPlus />} onClick={() => setShowCreate(true)}>
            Log new request
          </Button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['pending', 'in_progress', 'extended', 'fulfilled', 'rejected', 'all'].map((s) => (
            <Button key={s} size="sm" variant={filter === s ? 'primary' : 'ghost'} onClick={() => setFilter(s)}>
              {s === 'all' ? 'All' : s.replace('_', ' ')}
            </Button>
          ))}
          <div style={{ flex: 1 }} />
          <CsvExportButton rows={filtered} columns={csvColumns} filename="dierend-sar-requests" />
        </div>

        {error && <div style={{ color: '#f87171', marginBottom: 12 }}>{error}</div>}

        {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading…</div> : (
          <Table columns={columns} data={pageData} loading={loading}
            emptyMessage={`No ${filter === 'all' ? '' : filter} SARs`} />
        )}

        <Pagination
          page={page} pageSize={pageSize} pageCount={pageCount}
          totalItems={totalItems}
          onPageChange={setPage} onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="Log new SAR"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button variant="primary" loading={creating} onClick={handleCreate}>Create</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>User UID</span>
            <Input value={draftUserId} onChange={(e) => setDraftUserId(e.target.value)}
              placeholder="firebase Auth uid of the requester" />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>Request type</span>
            <select value={draftType} onChange={(e) => setDraftType(e.target.value)}
              style={{ background: '#0f1218', border: '1px solid #2a2f3a', borderRadius: 6, padding: '8px 10px', color: '#e8eaed' }}>
              {SAR_TYPES.map((t) => (
                <option key={t} value={t}>{SAR_TYPE_LABELS[t] || t}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>Source</span>
            <select value={draftSource} onChange={(e) => setDraftSource(e.target.value)}
              style={{ background: '#0f1218', border: '1px solid #2a2f3a', borderRadius: 6, padding: '8px 10px', color: '#e8eaed' }}>
              <option value="email">Email</option>
              <option value="in_app">In-app</option>
              <option value="dpo_inbox">DPO inbox</option>
              <option value="postal">Postal mail</option>
              <option value="manual">Manual / other</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>Notes (optional)</span>
            <textarea rows={3} value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)}
              placeholder="Verbatim quote of the request, ticket number, etc."
              style={{ background: '#0f1218', border: '1px solid #2a2f3a', borderRadius: 6, padding: '8px 10px', color: '#e8eaed', fontFamily: 'inherit', fontSize: 13 }} />
          </label>
          <p style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
            A 30-day deadline is set automatically. Use "Extend" to grant the additional 60 days
            allowed by GDPR Art. 12(3) for complex requests; the user must be informed of any extension.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Sar;
